import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { Users, Flower2, UserPlus2, Heart, Calendar, Plus } from "lucide-react";
import { useAuth } from "@/components/AuthProvider";
import { GUEST_GREETING, FOUNDING_BANNER } from "@/lib/levels";
import { IKO } from "@/design-system/tokens";
import { CompactCard } from "@/pages/member/feedCard";
import { RoseRow } from "@/design-system/RoseRow";

/**
 * Wave C.22-C.26 (Vania 7 Oct 2026 · onboarding mockup) · SUPERSEDES the old
 * L2/L3 (which was held under W-C.1/2/3/20). Flow:
 *   L1 splash  → L2.1-L2.6 (6 real-component slides) → L3 insider pitch →
 *   L3a get in → L4 you're in → L5 insider sheet (existing).
 *
 * Rules carried from the brief:
 *   · REAL app components only (CompactCard, RoseRow, IkoMix). No hand-drawn
 *     shapes. No designer notes inside screens.
 *   · Sample people: achieng odhiambo, lukas berger, priya shah, fatuma,
 *     kiprono, tom. People never carry an iko colour. Avatars are photo
 *     fallbacks on a neutral colour.
 *   · Say events / brunch / match day / gig · never default to nights.
 *   · "skip" (top right) on any slide → L3 (insider pitch).
 *   · "not now, just browse" closes the whole intro and parks the member on
 *     the current /app route as a guest.
 *
 * Six intro slides are rendered on L2 with six progress segments, back
 * bottom left, next bottom right. L2.3 + L2.5 use the OUTLINE next button
 * because the yellow is already spent in the slide itself.
 */
const META = "var(--ww-text-muted)";
const FLAG_KEY = "ww.guestIntroV3";

const SLIDE_EVENTS = {
  // W-C.34 (Vania 7 Oct 2026 · approved mockup) · every onboarding event is
  // INVENTED. No real events, curators, venues or handles anywhere in
  // GuestIntro. Dates are hard-coded ISO strings tied to oct 2026 so the
  // CompactCard meta line reads the exact copy from the brief:
  //   warehouse fridays → "fri 9 oct · 9:00pm · westlands"
  //   estate cup final  → "sat 3 oct · 2:00pm · ngong road"
  //   open studio sat   → "sat 3 oct · 5:00pm · lavington"
  // Photos live under /assets/brand/photography/ (web derivatives generated
  // from Vania's Drive masters by /app/scripts/generate_photo_web_copies.py).
  warehouse: {
    id: "intro:warehouse", iko: "sherehe",
    title: "warehouse fridays",
    start_at: "2026-10-09T21:00:00+03:00",
    end_at:   "2026-10-10T00:00:00+03:00",
    venue: "westlands", venue_name: "westlands",
    organiser_handle: "warehousefridays.ke",
    cover_image_url: "/assets/brand/photography/photo_sherehe_peak_01.web.jpeg",
    _cover_fallback: "/assets/brand/patterns/patsherehe.png",
    _cover_alt: "a club floor, hands up, pink light",
    price_min: 1500, price_max: 2500, is_free: false, ticket_link: "",
    going_count: 14,
    // L2.2 approved copy pieces · consumed by RealEventCard. The CompactCard
    // "N going" line is suppressed via `_hide_going` so only the attendee
    // strip under the card shows (W-C.35 · Vania 7 Oct · Claude QA round 3).
    _hide_going: true,
    _attendee_strip: "achieng, tom +12 going",
    _pill_label: "tomorrow's pick",
  },
  estateCup: {
    id: "intro:estate-cup", iko: "michezo",
    title: "estate cup final",
    start_at: "2026-10-03T14:00:00+03:00",
    end_at:   "2026-10-03T17:00:00+03:00",
    venue: "ngong road", venue_name: "ngong road",
    organiser_handle: "",
    cover_image_url: "/assets/brand/photography/photo_michezo_speed_02.web.jpeg",
    _cover_fallback: "/assets/brand/patterns/patmichezo.png",
    _cover_alt: "a player on the ball, the crowd behind the fence",
    is_free: false,
  },
  openStudio: {
    id: "intro:open-studio", iko: "polite",
    title: "open studio saturday",
    start_at: "2026-10-03T17:00:00+03:00",
    end_at:   "2026-10-03T20:00:00+03:00",
    venue: "lavington", venue_name: "lavington",
    organiser_handle: "",
    cover_image_url: "/assets/brand/photography/photo_polite_gallery_02.web.jpeg",
    _cover_fallback: "/assets/brand/patterns/patpolite.png",
    _cover_alt: "a gallery, people standing with the art",
  },
};

const SAMPLE_WATU = [
  { user_id: "w1", display_name: "achieng odhiambo", handle: "achieng",
    bio: "front row at every gig",
    counts: { sherehe: 11, michezo: 4, polite: 2, kazi: 1 } },
  { user_id: "w2", display_name: "lukas berger", handle: "lukasb",
    bio: "here for the football",
    counts: { michezo: 9, kazi: 4, sherehe: 2, polite: 1 } },
  { user_id: "w3", display_name: "priya shah", handle: "priyash",
    bio: "gallery openings, always",
    counts: { polite: 10, sherehe: 5, kazi: 2, michezo: 0 } },
];

export default function GuestIntro() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [done, setDone] = useState(() => {
    try { return !!localStorage.getItem(FLAG_KEY); } catch { return false; }
  });
  // L1 splash → L2.1..L2.6 → L3 pitch → L3a getin → L4 welcome
  const [stage, setStage] = useState("splash");
  const [idx, setIdx] = useState(1); // 1..6

  const show = useMemo(() => {
    if (loading) return false;
    if (user) return false;
    if (done) return false;
    const p = location.pathname || "/";
    if (!p.startsWith("/app")) return false;
    if (p.startsWith("/app/signin") || p.startsWith("/app/membership")) return false;
    return true;
  }, [loading, user, done, location.pathname]);

  // W-C.17 hook · guests tapping gated actions re-enter the intro at L3.
  useEffect(() => {
    const onOpen = () => {
      if (user) return;
      try { localStorage.removeItem(FLAG_KEY); } catch {}
      setDone(false);
      setStage("pitch");
    };
    window.addEventListener("ww:getin:open", onOpen);
    return () => window.removeEventListener("ww:getin:open", onOpen);
  }, [user]);

  const finish = () => {
    try { localStorage.setItem(FLAG_KEY, "1"); } catch {}
    setDone(true);
  };
  const skipToPitch = () => setStage("pitch");
  const next = () => {
    if (stage === "splash") { setStage("slides"); setIdx(1); return; }
    if (stage === "slides") {
      if (idx >= 6) { setStage("pitch"); return; }
      setIdx((i) => i + 1); return;
    }
    if (stage === "pitch") { setStage("getin"); return; }
    if (stage === "getin") { nav("/app/signin"); finish(); return; }
    if (stage === "welcome") { finish(); nav("/app"); return; }
  };
  const back = () => {
    if (stage === "slides" && idx > 1) { setIdx((i) => i - 1); return; }
    if (stage === "slides" && idx === 1) { setStage("splash"); return; }
    if (stage === "pitch") { setStage("slides"); setIdx(6); return; }
    if (stage === "getin") { setStage("pitch"); return; }
  };

  if (!show) return null;

  // L1 splash ----------------------------------------------------------
  if (stage === "splash") {
    return (
      <Scrim testId="guest-intro-splash">
        <div style={{ flex: 1, display: "flex", flexDirection: "column",
                       alignItems: "center", justifyContent: "center",
                       padding: "32px 24px", textAlign: "center", gap: 24 }}>
          <div data-testid="guest-intro-splash-logo"
               aria-label="watu wako"
               style={{ fontFamily: "Poppins", fontWeight: 800, fontSize: 24,
                         letterSpacing: "-0.02em", color: "var(--ww-text)",
                         textTransform: "lowercase" }}>
            watu wako
          </div>
          <div>
            <p data-testid="guest-intro-splash-mgeni"
               style={{ fontFamily: "Poppins", fontWeight: 800, fontSize: 42, lineHeight: 1.0,
                        letterSpacing: "-0.03em", color: "var(--ww-text)" }}>
              {GUEST_GREETING}
            </p>
            <p data-testid="guest-intro-splash-english"
               style={{ marginTop: 12, fontFamily: "Arial", color: META, fontSize: 15 }}>
              welcome, our guest
            </p>
          </div>
        </div>
        {/* W-C.32 (Vania 7 Oct) · L1 no longer auto-advances. Full width
            primary "continue" at the bottom · taps go to L2.1. */}
        <div style={{ padding: "0 20px 24px" }}>
          <button data-testid="guest-intro-continue" onClick={next}
                  style={{ ...pillPrimary, width: "100%" }}>
            continue
          </button>
        </div>
        <TopSkip onSkip={skipToPitch} />
      </Scrim>
    );
  }

  // L2.1..L2.6 ---------------------------------------------------------
  if (stage === "slides") {
    return (
      <Scrim testId="guest-intro">
        <TopSkip onSkip={skipToPitch} />
        <ProgressBar segments={6} active={idx} />
        <div style={{ flex: 1, display: "flex", flexDirection: "column",
                       padding: "28px 20px 20px", gap: 20, overflowY: "auto" }}>
          <SlideBody idx={idx} />
        </div>
        <BottomRow onBack={back} onNext={next}
                   nextLabel={idx === 6 ? "see what's next" : "next"}
                   outlineNext={idx === 3 || idx === 5} />
      </Scrim>
    );
  }

  // L3 insider pitch ---------------------------------------------------
  if (stage === "pitch") {
    return (
      <Scrim testId="guest-intro-insider-pitch">
        <TopSkip onSkip={() => setStage("getin")} />
        <div style={{ flex: 1, overflowY: "auto", padding: "28px 20px 20px",
                       display: "flex", flexDirection: "column", gap: 18 }}>
          <h1 className="lowercase"
              data-testid="insider-pitch-title"
              style={{ fontFamily: "Poppins", fontWeight: 800, fontSize: 38,
                        letterSpacing: "-0.03em", lineHeight: 1.05, color: "var(--ww-text)" }}>
            your people are already planning
          </h1>
          <ActivityRow />
          <p className="lowercase" style={{ fontFamily: "Arial", fontSize: 15, color: META, lineHeight: 1.5 }}>
            become an insider · everything in get in, plus
          </p>
          <PerksGrid />
          <p style={{ fontFamily: "Arial", fontSize: 12, color: META, textAlign: "center" }}>
            curators are the people who put on your events · <span className="ww-currency">KES</span> 200 a month for life · {FOUNDING_BANNER.trialLine}
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 4 }}>
            <button data-testid="insider-pitch-primary" onClick={() => { finish(); nav("/app/signin?next=/app/membership"); }}
                    style={pillPrimary}>
              become an insider
            </button>
            <button data-testid="insider-pitch-tertiary" onClick={() => setStage("getin")}
                    style={pillGhost}>
              just get in, it's free
            </button>
            <button data-testid="insider-pitch-browse" onClick={() => { finish(); }}
                    style={textLink}>
              not now, just browse
            </button>
          </div>
        </div>
        <BottomRow onBack={back} onNext={null} />
      </Scrim>
    );
  }

  // L3a get in ---------------------------------------------------------
  if (stage === "getin") {
    return (
      <Scrim testId="get-in-screen">
        <div style={{ flex: 1, overflowY: "auto", padding: "32px 24px 24px",
                       display: "flex", flexDirection: "column", gap: 18, maxWidth: 520, margin: "0 auto", width: "100%" }}>
          <div data-testid="get-in-logo" aria-label="watu wako"
               style={{ fontFamily: "Poppins", fontWeight: 800, fontSize: 20, letterSpacing: "-0.02em",
                        color: "var(--ww-text)", textTransform: "lowercase" }}>
            watu wako
          </div>
          <h1 className="lowercase" data-testid="get-in-title"
              style={{ fontFamily: "Poppins", fontWeight: 800, fontSize: 56,
                        letterSpacing: "-0.03em", lineHeight: 0.95, color: "var(--ww-text)" }}>
            get in
          </h1>
          <p data-testid="get-in-body" style={{ fontFamily: "Arial", fontSize: 16, color: META, lineHeight: 1.5 }}>
            it's free. keep the events you're interested in, see who's going and message your people.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 6 }}>
            <button data-testid="get-in-google" onClick={() => { finish(); nav("/app/signin"); }}
                    style={pillPrimary}>
              continue with google
            </button>
            <button data-testid="get-in-phone" onClick={() => { finish(); nav("/app/signin?mode=phone"); }}
                    style={pillGhost}>
              continue with your phone
            </button>
          </div>
          <p data-testid="get-in-legal" style={{ fontFamily: "Arial", fontSize: 11, color: META, marginTop: 8, textAlign: "center", lineHeight: 1.5 }}>
            by getting in you agree to our terms and privacy policy. we never post anything for you.
          </p>
        </div>
        <BottomRow onBack={back} onNext={null} />
      </Scrim>
    );
  }

  // L4 welcome ---------------------------------------------------------
  if (stage === "welcome") {
    return (
      <Scrim testId="youre-in-screen">
        <div style={{ flex: 1, display: "grid", placeItems: "center", padding: 24, textAlign: "center" }}>
          <div style={{ maxWidth: 460 }}>
            <h1 className="lowercase" data-testid="youre-in-title"
                style={{ fontFamily: "Poppins", fontWeight: 800, fontSize: 56,
                          letterSpacing: "-0.03em", lineHeight: 0.95 }}>
              you're in
            </h1>
            <p data-testid="youre-in-body"
               style={{ marginTop: 16, fontFamily: "Arial", fontSize: 16, color: META, lineHeight: 1.5 }}>
              karibu. it all starts here: keep what you're interested in, see who's going, message your people.
            </p>
            <button data-testid="youre-in-cta" onClick={() => { finish(); nav("/app"); }}
                    style={{ ...pillPrimary, marginTop: 24 }}>
              show me what's on
            </button>
          </div>
        </div>
      </Scrim>
    );
  }

  return null;
}

// -----------------------------------------------------------------------
// Scrim / chrome
// -----------------------------------------------------------------------
function Scrim({ children, testId }) {
  return (
    <div role="dialog" aria-modal="true" data-testid={testId}
         style={{ position: "fixed", inset: 0, zIndex: 70,
                  background: "var(--ww-ground)", color: "var(--ww-text)",
                  display: "flex", flexDirection: "column" }}>
      {children}
    </div>
  );
}
function TopSkip({ onSkip }) {
  return (
    <div style={{ position: "absolute", top: 14, right: 16, zIndex: 2 }}>
      <button data-testid="guest-intro-skip" onClick={onSkip}
              style={{ background: "transparent", border: 0, cursor: "pointer",
                       color: "var(--ww-text-muted)", fontFamily: "Arial", fontSize: 13,
                       textTransform: "lowercase", padding: "8px 10px" }}>
        skip
      </button>
    </div>
  );
}
function ProgressBar({ segments, active }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${segments}, 1fr)`,
                   gap: 6, padding: "16px 16px 0" }}>
      {Array.from({ length: segments }).map((_, i) => (
        <span key={i}
              data-testid={`guest-intro-progress-${i + 1}`}
              style={{ height: 3, borderRadius: 2,
                        background: i + 1 <= active ? "var(--ww-text)" : "var(--ww-line)" }} />
      ))}
    </div>
  );
}
function BottomRow({ onBack, onNext, nextLabel = "next", outlineNext = false }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                   padding: "12px 16px 20px" }}>
      {onBack ? (
        <button data-testid="guest-intro-back" onClick={onBack}
                style={{ background: "transparent", border: 0, cursor: "pointer",
                         color: "var(--ww-text-muted)", fontFamily: "Arial", fontSize: 14,
                         textTransform: "lowercase", padding: "8px 10px",
                         display: "inline-flex", alignItems: "center", gap: 4 }}>
          <ChevronLeft size={14} /> back
        </button>
      ) : <span />}
      {onNext ? (
        <button data-testid="guest-intro-next" onClick={onNext}
                style={outlineNext ? pillGhost : pillPrimary}>
          {nextLabel}
        </button>
      ) : <span />}
    </div>
  );
}

// -----------------------------------------------------------------------
// Slide bodies (L2.1 – L2.6) · real components only.
// -----------------------------------------------------------------------
function SlideBody({ idx }) {
  if (idx === 1) {
    return (
      <SlideFrame line="every event in nairobi, in one place"
                   fact="sherehe, polite, michezo and kazi. hear about the best brunches, gigs and match days before they happen, not after."
                   testId="intro-slide-1">
        <IkoGrid />
      </SlideFrame>
    );
  }
  if (idx === 2) {
    return (
      <SlideFrame line="see who's going before you go"
                   fact="88% of us trust friends over any app. so we show you your people first."
                   testId="intro-slide-2">
        <RealEventCard key="e-warehouse" ev={SLIDE_EVENTS.warehouse} testId="intro-real-event-card" />
      </SlideFrame>
    );
  }
  if (idx === 3) {
    return (
      <SlideFrame line="plan it with your squad"
                   fact="43% of us skip a gig, a game or a brunch because there's nobody to go with. not any more."
                   testId="intro-slide-3">
        <SquadBubbles />
      </SlideFrame>
    );
  }
  if (idx === 4) {
    return (
      <SlideFrame line="honest scores from people who went"
                   fact="7 in 10 of us have paid for a day out or a night out that let us down. know before you go."
                   testId="intro-slide-4">
        <RealEventCard key="e-estate" ev={SLIDE_EVENTS.estateCup}
                       scoreBelow={{ value: 4.3, mode: "number" }}
                       scoreCaption="from 23 people who went"
                       testId="intro-real-event-card" />
      </SlideFrame>
    );
  }
  if (idx === 5) {
    return (
      <SlideFrame line="the events we've awarded"
                   fact="roses from watu wako. we pay our own entry, and nobody can buy one."
                   testId="intro-slide-5">
        <RealEventCard key="e-open-studio" ev={SLIDE_EVENTS.openStudio}
                       scoreBelow={{ value: 4.8, mode: "roses" }}
                       testId="intro-real-event-card" />
      </SlideFrame>
    );
  }
  if (idx === 6) {
    return (
      <SlideFrame line="find your people"
                   fact="follow the watu who love the same mornings, afternoons and nights you do."
                   testId="intro-slide-6">
        <WatuList />
      </SlideFrame>
    );
  }
  return null;
}
function SlideFrame({ line, fact, children, testId }) {
  return (
    <div data-testid={testId}
         style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ display: "grid", placeItems: "center" }}>{children}</div>
      <h2 className="lowercase"
          style={{ fontFamily: "Poppins", fontWeight: 800, fontSize: 32,
                   letterSpacing: "-0.03em", lineHeight: 1.05, color: "var(--ww-text)" }}>
        {line}
      </h2>
      <p style={{ fontFamily: "Arial", fontSize: 14, color: META, lineHeight: 1.5 }}>
        {fact}
      </p>
    </div>
  );
}
function IkoGrid() {
  // W-C.33 (Vania 7 Oct · correction) · rubric illustrations · one per iko ·
  // 2×2 with the iko eyebrow under each. No pattern tile behind, no caption.
  // Image slot reads from /assets/brand/illustrations/ill-vibe-{iko}.jpeg,
  // sourced from Vania's Drive masters. While the files are not yet in the
  // repo, each slot falls back to the iko bright fill so the layout is
  // intact for QA. Alt text is final · never changes with the master swap.
  const keys = ["sherehe", "polite", "michezo", "kazi"];
  const alts = {
    sherehe: "a day party crowd dancing, arms up, under a low sun",
    polite:  "a seated audience listening, a steward at the side",
    michezo: "a stand on its feet, one scarf held overhead",
    kazi:    "a room mid session, lanyards on",
  };
  const onMissing = (e) => {
    // Hide the broken-image icon; the fallback fill carries the slot.
    try { e.currentTarget.style.visibility = "hidden"; } catch {}
  };
  return (
    <div data-testid="intro-iko-grid"
         style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12,
                   width: "100%", maxWidth: 360 }}>
      {keys.map((k) => (
        <figure key={k} style={{ margin: 0, display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={{ width: "100%", aspectRatio: "900 / 604",
                         borderRadius: 14, overflow: "hidden",
                         border: "1px solid var(--ww-line)",
                         background: IKO[k]?.bright || "var(--ww-sunken)",
                         position: "relative" }}>
            <img src={`/assets/brand/illustrations/ill-vibe-${k}.web.jpeg`}
                 alt={alts[k]}
                 onError={onMissing}
                 data-testid={`intro-iko-illustration-${k}`}
                 style={{ width: "100%", height: "100%",
                          objectFit: "cover", display: "block" }} />
          </div>
          <figcaption data-testid={`intro-iko-tile-${k}`}
                       style={{ fontFamily: "Arial", fontSize: 10,
                                 color: "var(--iko-eyebrow)",
                                 letterSpacing: "0.22em", textTransform: "uppercase" }}>
            {k}
          </figcaption>
        </figure>
      ))}
    </div>
  );
}
function RealEventCard({ ev, scoreBelow, scoreCaption, testId }) {
  // W-C.34 (Vania 7 Oct) · onboarding event cards wear brand photography.
  const onPhotoErr = (e) => {
    try {
      if (ev._cover_fallback) e.currentTarget.src = ev._cover_fallback;
    } catch {}
  };
  // W-C.34 round 2 · optional pill ("tomorrow's pick") above the card and
  // an attendee strip ("achieng, tom +12 going") under it. Both come from
  // the fixed-copy SLIDE_EVENTS object · nothing is computed from now.
  return (
    <div style={{ width: "100%", maxWidth: 420, display: "flex", flexDirection: "column", gap: 10 }}>
      {ev._pill_label && (
        <span data-testid="intro-event-pill"
              style={{ alignSelf: "flex-start", padding: "4px 10px",
                       borderRadius: 999, background: IKO[ev.iko]?.bright,
                       color: "#FFFFFF", fontFamily: "Poppins", fontWeight: 700,
                       fontSize: 11, textTransform: "lowercase",
                       letterSpacing: "0.02em" }}>
          {ev._pill_label}
        </span>
      )}
      <div data-testid={testId} style={{ position: "relative" }}>
        <CompactCard event={ev} preview />
        {ev.cover_image_url && (
          <img src={ev.cover_image_url}
               alt={ev._cover_alt || ""}
               onError={onPhotoErr}
               data-testid="intro-event-photo"
               data-slot={`photography-${ev.iko}`}
               style={{ display: "none" }} />
        )}
      </div>
      {ev._attendee_strip && (
        <p data-testid="intro-attendee-strip"
           style={{ fontFamily: "Arial", fontSize: 12, color: META, marginTop: -4 }}>
          {ev._attendee_strip}
        </p>
      )}
      {scoreBelow && (
        <div data-testid={scoreBelow.mode === "roses" ? "intro-award-roses" : "intro-score"}
             style={{ display: "flex", alignItems: "center", gap: 10,
                       padding: "10px 12px", borderRadius: 12,
                       background: "var(--ww-surface)", border: "1px solid var(--ww-line)" }}>
          {/* W-C.40/41 (Vania 8 Oct correction) · slide 4 (mode "number")
              renders the ink-pill NUMBER ONLY, no roses. Slide 5 (mode
              "roses") renders the ROSES ONLY, no number. The D35 lookup
              inside RoseRow (W-C.37) still drives the rose count. */}
          <RoseRow value={scoreBelow.value} colourway="yellow" size={20}
                   ground="cream" demo
                   showNumber={scoreBelow.mode === "number"}
                   showRoses={scoreBelow.mode === "roses"}
                   testId="intro-score-row" />
          {scoreCaption && (
            <span style={{ fontFamily: "Arial", fontSize: 12, color: META }}>
              {scoreCaption}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
function SquadBubbles() {
  // W-C.34 (Vania 7 Oct) · the shared card title + photo match L2.2 ·
  // "warehouse fridays" · photo_sherehe_peak_01. The chat snippet reads
  // "anyone for warehouse fridays this week?" verbatim from the brief.
  // W-C.34 round 2 (Vania 7 Oct) · the L2.3 shared card copy mirrors L2.2
  // but with NO going count (brief: "L2.3 shared card: no going count").
  const sharedCard = { ...SLIDE_EVENTS.warehouse, going_count: 0, _hide_going: true, _pill_label: null };
  const rows = [
    { who: "fatuma", text: "anyone for warehouse fridays this week?", mine: false },
    { who: "kiprono", text: null, event: sharedCard, mine: false },
    { who: "lukas", text: "i'm in. meet at the gate at 8?", mine: false },
    { who: "you", text: "i'll get the tickets", mine: true },
  ];
  return (
    <div data-testid="intro-squad-thread"
         style={{ width: "100%", maxWidth: 420, display: "flex", flexDirection: "column", gap: 10,
                   padding: 14, borderRadius: 16, background: "var(--ww-surface)",
                   border: "1px solid var(--ww-line)" }}>
      <p style={{ fontFamily: "Poppins", fontWeight: 700, fontSize: 13,
                  textTransform: "lowercase", color: "var(--ww-text)" }}>
        friday crew · 5 people
      </p>
      {rows.map((r, i) => (
        <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-end",
                                justifyContent: r.mine ? "flex-end" : "flex-start" }}>
          {!r.mine && <Avatar who={r.who} />}
          <div style={{ display: "flex", flexDirection: "column",
                         alignItems: r.mine ? "flex-end" : "flex-start", maxWidth: "80%" }}>
            {!r.mine && (
              <span data-testid="yap-msg-name"
                    style={{ fontFamily: "Arial", fontSize: 11, color: META, marginBottom: 2 }}>
                {r.who}
              </span>
            )}
            {r.event ? (
              <div style={{ maxWidth: 280 }}><CompactCard event={r.event} preview /></div>
            ) : (
              <span style={{ background: r.mine ? "#FBF4E9" : "var(--ww-sunken)",
                              color: r.mine ? "#1A1A30" : "var(--ww-text)",
                              padding: "8px 12px", borderRadius: 14,
                              fontFamily: "Arial", fontSize: 13 }}>
                {r.text}
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
function Avatar({ who }) {
  const initial = (who || "?")[0].toLowerCase();
  return (
    <span data-testid="yap-msg-avatar"
          style={{ width: 28, height: 28, borderRadius: 999,
                    background: "var(--ww-sunken)", color: "var(--ww-text)",
                    display: "grid", placeItems: "center",
                    fontFamily: "Poppins", fontWeight: 800, fontSize: 12,
                    flexShrink: 0 }}>
      {initial}
    </span>
  );
}
// WW-176 · onboarding sample people get real photos; initial circle if the photo fails.
const WATU_PHOTOS = { w1: "/onboarding/achieng.jpg", w2: "/onboarding/lukas.jpg", w3: "/onboarding/priya.jpg" };
function WatuPhoto({ m }) {
  const [failed, setFailed] = useState(false);
  const src = WATU_PHOTOS[m.user_id];
  if (failed || !src) {
    return (
      <span data-testid={`intro-watu-photo-fallback-${m.user_id}`}
            style={{ width: 48, height: 48, borderRadius: 999, flex: "none",
                      background: "var(--ww-sunken)", color: "var(--ww-text)",
                      display: "grid", placeItems: "center",
                      fontFamily: "Poppins", fontWeight: 800, fontSize: 18 }}>
        {(m.display_name || "?")[0].toLowerCase()}
      </span>
    );
  }
  return (
    <img data-testid={`intro-watu-photo-${m.user_id}`} src={src} alt={m.display_name}
         width={48} height={48} onError={() => setFailed(true)}
         style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 999, flex: "none" }} />
  );
}
function WatuMixBar({ counts }) {
  const ranked = Object.entries(counts || {}).filter(([, n]) => n > 0).sort((a, b) => b[1] - a[1]);
  const sum = ranked.reduce((a, [, n]) => a + n, 0);
  if (!sum) return null;
  const label = `iko mix: ${ranked.map(([k, n]) => `${k} ${Math.round((n / sum) * 100)}%`).join(", ")}`;
  return (
    <div data-testid="watu-card-iko-mix" role="img" aria-label={label}
         style={{ display: "flex", width: "100%", height: 4, borderRadius: 2, overflow: "hidden",
                   marginTop: 10, background: "var(--ww-line)" }}>
      {ranked.map(([k, n]) => (
        <span key={k} style={{ width: `${(n / sum) * 100}%`, height: "100%", background: IKO[k]?.bright || "var(--ww-line)" }} />
      ))}
    </div>
  );
}
function WatuList() {
  return (
    <div data-testid="intro-watu-list"
         style={{ width: "100%", maxWidth: 420, display: "flex", flexDirection: "column", gap: 10 }}>
      {SAMPLE_WATU.map((m) => (
        <div key={m.user_id} data-testid={`intro-watu-card-${m.user_id}`}
             style={{ display: "flex", gap: 12, padding: "14px 14px 16px", borderRadius: 14,
                       background: "var(--ww-surface)", border: "1px solid var(--ww-line)",
                       alignItems: "center" }}>
          <WatuPhoto m={m} />
          <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
            <div style={{ display: "flex", gap: 8, alignItems: "baseline",
                           justifyContent: "space-between" }}>
              <span style={{ fontFamily: "Poppins", fontWeight: 700, fontSize: 14,
                              textTransform: "lowercase", color: "var(--ww-text)",
                              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {m.display_name}
              </span>
              <button style={{ border: "1px solid var(--ww-line-strong)",
                                background: "transparent", color: "var(--ww-text)",
                                borderRadius: 999, padding: "4px 10px",
                                fontFamily: "Poppins", fontWeight: 700, fontSize: 11,
                                textTransform: "lowercase", cursor: "pointer" }}>
                follow
              </button>
            </div>
            <span style={{ fontFamily: "Arial", fontSize: 12, color: META, marginTop: 2 }}>@{m.handle} · {m.bio}</span>
            <WatuMixBar counts={m.counts} />
          </div>
        </div>
      ))}
    </div>
  );
}

// -----------------------------------------------------------------------
// L3 insider pitch pieces
// -----------------------------------------------------------------------
function ActivityRow() {
  const [row, setRow] = useState(null);
  useEffect(() => {
    // Try the public squads-live hint endpoint; if it returns nothing, hide.
    fetch("/api/public/squads/activity")
      .then((r) => r.ok ? r.json() : null)
      .then((d) => { if (d && d.row) setRow(d.row); })
      .catch(() => {});
  }, []);
  if (!row) return null;
  return (
    <div data-testid="insider-pitch-activity"
         style={{ padding: "12px 14px", borderRadius: 12,
                   background: "var(--ww-surface)", border: "1px solid var(--ww-line)",
                   display: "flex", alignItems: "center", gap: 10 }}>
      <AvatarStack n={Math.min(2, row.avatars_count || 2)} />
      <span style={{ fontFamily: "Arial", fontSize: 13, color: "var(--ww-text)" }}>
        {row.text}
      </span>
    </div>
  );
}
function AvatarStack({ n }) {
  return (
    <span style={{ display: "inline-flex" }}>
      {Array.from({ length: n }).map((_, i) => (
        <span key={i} style={{ width: 24, height: 24, borderRadius: 999,
                                 background: "var(--ww-sunken)",
                                 marginLeft: i === 0 ? 0 : -8,
                                 border: "2px solid var(--ww-surface)" }} />
      ))}
    </span>
  );
}
function PerksGrid() {
  // W-C.24 / W-C.30 (Vania 7 Oct · update) · each perk tile carries a WHITE
  // icon ABOVE the label (stroke 2.4, #FFFFFF) on the iko bright fill. One
  // icon per perk, from the mockup.
  const PERKS = [
    { k: "join squads",          iko: "michezo", Icon: Users },
    { k: "real reviews",         iko: "sherehe", Icon: Flower2 },
    { k: "follow curators",      iko: "polite",  Icon: UserPlus2 },
    { k: "insiders' favourites", iko: "kazi",    Icon: Heart },
    { k: "going and i went",     iko: "polite",  Icon: Calendar },
    { k: "add your events",      iko: "michezo", Icon: Plus },
  ];
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
      {PERKS.map((p, i) => (
        <div key={p.k}
             data-testid={`insider-pitch-perk-${i + 1}`}
             style={{ padding: "14px 10px", borderRadius: 14, minHeight: 92,
                       background: IKO[p.iko]?.bright, color: "#FFFFFF",
                       display: "flex", flexDirection: "column",
                       alignItems: "flex-start", justifyContent: "space-between",
                       gap: 10, textAlign: "left",
                       fontFamily: "Poppins", fontWeight: 700, fontSize: 13,
                       textTransform: "lowercase" }}>
          <p.Icon size={22} color="#FFFFFF" strokeWidth={2.4} aria-hidden
                   data-testid={`insider-pitch-perk-${i + 1}-icon`} />
          <span style={{ color: "#1A1A30" }}>{p.k}</span>
        </div>
      ))}
    </div>
  );
}

// -----------------------------------------------------------------------
// Button styles
// -----------------------------------------------------------------------
const pillPrimary = {
  padding: "14px 20px", borderRadius: 999,
  background: "#FFD60A", color: "#1A1A30", border: "1px solid #FFD60A",
  fontFamily: "Poppins", fontWeight: 800, fontSize: 15,
  textTransform: "lowercase", cursor: "pointer", minHeight: 48,
};
const pillGhost = {
  padding: "14px 20px", borderRadius: 999,
  background: "transparent", color: "var(--ww-text)",
  border: "1px solid var(--ww-line-strong)",
  fontFamily: "Poppins", fontWeight: 700, fontSize: 14,
  textTransform: "lowercase", cursor: "pointer", minHeight: 48,
};
const textLink = {
  padding: "10px", background: "transparent", color: "var(--ww-text-muted)",
  border: 0, cursor: "pointer",
  fontFamily: "Arial", fontSize: 13, textTransform: "lowercase",
  textDecoration: "underline",
};
